import { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Segment,
  Header,
  Message,
  Form,
  Loader,
  Dimmer,
  Label,
  List,
} from "semantic-ui-react";
import { fetchUserAttributes } from "@aws-amplify/auth";
import axios from "../../../apis/dvdlocator";
import { PlatformListingFields } from "./PlatformListingFields";
import {
  EMPTY_IDENTIFIERS,
  LISTING_TITLE_MAX_LENGTH,
  buildListingPayload,
  describeListingIdentifiers,
  existingProductAsins,
  hasIdentifierErrors,
  listingSubmitErrorMessage,
  normalizeProductListings,
  platformLabel,
  validateListingTitle,
  validatePlatformIdentifiers,
  type ListingPlatform,
  type PlatformIdentifiers,
  type ProductListingSummary,
} from "./listingPayload";

interface ProductSearchResult {
  product_id: number;
  product_title: string;
}

interface CreateProductListingProps {
  productId?: number;
  productTitle?: string;
  onClose: (didCreate?: boolean) => void;
}

export function CreateProductListing({
  productId,
  productTitle,
  onClose,
}: CreateProductListingProps) {
  // Product search state (used when no productId prop is provided)
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<
    number | undefined
  >(undefined);
  const [selectedProductTitle, setSelectedProductTitle] = useState("");

  const effectiveProductId = productId ?? selectedProductId;
  const effectiveProductTitle = productTitle || selectedProductTitle;

  const needsSearch = productId === undefined;

  // Debounce the search query
  useEffect(() => {
    if (!needsSearch) return;
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery, needsSearch]);

  // Fetch search results
  useEffect(() => {
    if (!needsSearch || !debouncedQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const abortController = new AbortController();
    async function fetchResults() {
      setIsSearching(true);
      try {
        const res = await axios.get("/products", {
          params: { search: debouncedQuery.trim() },
          signal: abortController.signal,
        });
        setSearchResults(res.data ?? []);
      } catch {
        if (!abortController.signal.aborted) setSearchResults([]);
      } finally {
        if (!abortController.signal.aborted) setIsSearching(false);
      }
    }
    fetchResults();
    return () => abortController.abort();
  }, [debouncedQuery, needsSearch]);

  // Existing listings state
  const [existingListings, setExistingListings] = useState<
    ProductListingSummary[]
  >([]);
  const [existingAsins, setExistingAsins] = useState<Set<string>>(
    () => new Set()
  );
  const [loadingListings, setLoadingListings] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [createdBy, setCreatedBy] = useState("");
  const [createdOn] = useState(() => new Date().toLocaleString());

  useEffect(() => {
    fetchUserAttributes()
      .then((attrs) => {
        if (attrs.name) setCreatedBy(attrs.name);
      })
      .catch((err) => console.log(err));
  }, []);

  const [platform, setPlatform] = useState<ListingPlatform>("amazon");
  const [identifiers, setIdentifiers] =
    useState<PlatformIdentifiers>(EMPTY_IDENTIFIERS);
  const [listingTitle, setListingTitle] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!effectiveProductId) return;
    const abortController = new AbortController();

    async function fetchListings() {
      setLoadingListings(true);
      setLoadError(null);
      try {
        const res = await axios.get(`/product/${effectiveProductId}/listings`, {
          signal: abortController.signal,
        });
        setExistingListings(normalizeProductListings(res.data));
        setExistingAsins(existingProductAsins(res.data));
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          setLoadError(
            err.response?.data?.message ||
              "Failed to load existing listings. Please close and try again."
          );
        }
      } finally {
        if (!abortController.signal.aborted) setLoadingListings(false);
      }
    }

    fetchListings();
    return () => abortController.abort();
  }, [effectiveProductId]);

  // Identifiers are optional. Decision D1: an ASIN already on this product is
  // allowed (it adds another offer); the server answers 409 with a message
  // when the ASIN belongs to a different product.
  const listingTitleError = validateListingTitle(listingTitle);
  const identifierErrors = validatePlatformIdentifiers(platform, identifiers);
  const isValid = !listingTitleError && !hasIdentifierErrors(identifierErrors);

  const targetProductLabel = [`#${effectiveProductId}`, effectiveProductTitle]
    .filter(Boolean)
    .join(" ");

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!isValid || isSubmitting || !effectiveProductId) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(
        `/product/${effectiveProductId}/listing`,
        buildListingPayload({
          platform,
          listingTitle,
          listingOrigin: "create_listing",
          createdBy,
          identifiers,
        })
      );
      setSuccess(true);
    } catch (err: any) {
      setSubmitError(listingSubmitErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose()}
      closeOnDimmerClick={false}
      size="large"
    >
      <Modal.Header>Create a Listing</Modal.Header>
      <Modal.Content>
        {/* Product search (only when no productId prop provided) */}
        {needsSearch && !selectedProductId && (
          <>
            <Header as="h4" dividing>
              Search for a Product
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Search by product title or product ID to select a product.
            </p>
            <Form>
              <Form.Input
                placeholder="Search by title or product ID…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedProductId(undefined);
                  setSelectedProductTitle("");
                }}
                loading={isSearching}
                icon="search"
                iconPosition="left"
              />
            </Form>
            {searchResults.length > 0 && (
              <List
                divided
                selection
                style={{
                  marginTop: "0.5em",
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                {searchResults.map((result) => (
                  <List.Item
                    key={result.product_id}
                    onClick={() => {
                      setSelectedProductId(result.product_id);
                      setSelectedProductTitle(result.product_title);
                      setSearchQuery("");
                      setSearchResults([]);
                    }}
                    style={{ padding: "0.75em 1em", cursor: "pointer" }}
                  >
                    <List.Content>
                      <List.Header>
                        <Label size="tiny" horizontal>
                          ID {result.product_id}
                        </Label>
                        {result.product_title}
                      </List.Header>
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
            {debouncedQuery.trim() &&
              !isSearching &&
              searchResults.length === 0 && (
                <Segment textAlign="center" style={{ color: "#999" }}>
                  No products found
                </Segment>
              )}
          </>
        )}

        {/* Selected Product */}
        {effectiveProductId && (
          <>
            <Header as="h4" dividing>
              Selected Product
              {needsSearch && (
                <Button
                  size="mini"
                  basic
                  floated="right"
                  style={{ marginTop: "-4px" }}
                  onClick={() => {
                    setSelectedProductId(undefined);
                    setSelectedProductTitle("");
                    setExistingListings([]);
                    setExistingAsins(new Set());
                    setIdentifiers(EMPTY_IDENTIFIERS);
                    setListingTitle("");
                    setSubmitted(false);
                    setSuccess(false);
                  }}
                >
                  Change
                </Button>
              )}
            </Header>
            <Table definition compact>
              <Table.Body>
                <Table.Row>
                  <Table.Cell width={4}>Product ID</Table.Cell>
                  <Table.Cell>{effectiveProductId}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Product Title</Table.Cell>
                  <Table.Cell>{effectiveProductTitle}</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>

            {/* Existing listings */}
            <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
              Listings on This Product
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Review the product's current listings before adding a new one.
            </p>

            {loadError && (
              <Message negative>
                <p>{loadError}</p>
              </Message>
            )}

            {loadingListings ? (
              <Segment style={{ minHeight: "60px" }}>
                <Dimmer active inverted>
                  <Loader size="small">Loading listings…</Loader>
                </Dimmer>
              </Segment>
            ) : existingListings.length === 0 ? (
              <Segment textAlign="center" style={{ color: "#999" }}>
                No listings yet
              </Segment>
            ) : (
              <Table celled compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Platform</Table.HeaderCell>
                    <Table.HeaderCell>Identifiers</Table.HeaderCell>
                    <Table.HeaderCell>Title</Table.HeaderCell>
                    <Table.HeaderCell>Created By</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {existingListings.map((listing) => (
                    <Table.Row key={listing.key}>
                      <Table.Cell>
                        {platformLabel(listing.platform)}
                        {listing.isCompetitorListing && (
                          <Label
                            size="tiny"
                            horizontal
                            style={{ marginLeft: "0.5em" }}
                          >
                            Competitor
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {describeListingIdentifiers(listing)}
                      </Table.Cell>
                      <Table.Cell>{listing.listingTitle ?? "—"}</Table.Cell>
                      <Table.Cell>{listing.createdBy ?? "—"}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}

            {/* New Listing */}
            {!success && (
              <>
                <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
                  New Listing
                </Header>

                {submitError && (
                  <Message negative>
                    <p>{submitError}</p>
                  </Message>
                )}

                <p>
                  This listing will be attached to product{" "}
                  <strong>{targetProductLabel}</strong>.
                </p>

                <Form>
                  <PlatformListingFields
                    platform={platform}
                    onPlatformChange={(next) => {
                      setPlatform(next);
                      setSubmitError(null);
                    }}
                    identifiers={identifiers}
                    onIdentifiersChange={(next) => {
                      setIdentifiers(next);
                      setSubmitError(null);
                    }}
                    showErrors={submitted}
                    disabled={isSubmitting}
                    existingAsins={existingAsins}
                  />
                  <Form.Field required>
                    <label>Listing Title</label>
                    <Form.Input
                      placeholder="Enter Listing Title"
                      value={listingTitle}
                      onChange={(e) => {
                        setListingTitle(e.target.value);
                        setSubmitError(null);
                      }}
                      maxLength={LISTING_TITLE_MAX_LENGTH}
                      disabled={isSubmitting}
                    />
                    {submitted && listingTitleError && (
                      <Label basic color="red" pointing>
                        {listingTitleError}
                      </Label>
                    )}
                  </Form.Field>
                  <Form.Field>
                    <label>Created On</label>
                    <Form.Input disabled value={createdOn} readOnly />
                  </Form.Field>
                  <Form.Field>
                    <label>Created By</label>
                    <Form.Input disabled value={createdBy} readOnly />
                  </Form.Field>
                </Form>
              </>
            )}

            {success && (
              <Message positive>
                <Message.Header>
                  Listing created and attached to product #{effectiveProductId}.
                </Message.Header>
              </Message>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose(success)}>
          {success ? "Close" : "Cancel"}
        </Button>
        {effectiveProductId && !success && (
          <Button
            primary
            onClick={handleSubmit}
            disabled={isSubmitting || loadingListings}
            loading={isSubmitting}
          >
            Create Listing
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
